import {
  readWorkflowPublishedInspection,
  type WorkflowPublishedInspection,
  type WorkflowPublishedInspectionSource,
  type WorkflowSavedPresence,
} from './workflow-published-inspection';
import type {
  WorkflowPublishedWorkFact,
  WorkflowPublishedWorkDetailRow,
  WorkflowPublishedWorkDetailGroup,
  WorkflowPublishedWorkRow,
  WorkflowPublishedWorkSection,
  WorkflowPublishedWorkInspection,
} from './workflow-published-work-inspection';
export type WorkflowPublishedRoutingTarget =
  | { kind: 'node'; nodeIndex: number; nodeId: string | null }
  | { kind: 'edge'; edgeIndex: number; edgeId: string | null };
export type WorkflowPublishedRoutingSectionId =
  | 'paths'
  | 'condition'
  | 'commandResults'
  | 'automation'
  | 'terminalResult';
export type WorkflowPublishedRoutingFact = WorkflowPublishedWorkFact;
export type WorkflowPublishedRoutingDetailRow = WorkflowPublishedWorkDetailRow;
export type WorkflowPublishedRoutingDetailGroup =
  WorkflowPublishedWorkDetailGroup;
export type WorkflowPublishedRoutingRow = WorkflowPublishedWorkRow;
export interface WorkflowPublishedRoutingSection extends Omit<
  WorkflowPublishedWorkSection,
  'id'
> {
  id: WorkflowPublishedRoutingSectionId;
}
export interface WorkflowPublishedRoutingInspection {
  context: string;
  state: 'available' | 'unavailable';
  provenance: WorkflowPublishedInspection['provenance'];
  target: WorkflowPublishedRoutingTarget;
  element: {
    kind: 'node' | 'edge';
    index: number;
    id: string | null;
    label: string;
    issues: readonly string[];
    raw: unknown;
  } | null;
  node: WorkflowPublishedWorkInspection['node'];
  issues: readonly string[];
  sections: Readonly<
    Record<WorkflowPublishedRoutingSectionId, WorkflowPublishedRoutingSection>
  >;
}
type Saved = Record<string, unknown>;
type Field = readonly [string, string];
const record = (raw: unknown): raw is Saved =>
  !!raw && typeof raw === 'object' && !Array.isArray(raw);
const object = (raw: unknown): Saved => (record(raw) ? raw : {});
const presence = (owner: Saved, key: string): WorkflowSavedPresence =>
  !Object.hasOwn(owner, key)
    ? 'absent'
    : owner[key] === null
      ? 'null'
      : 'value';
const copy = (raw: unknown): unknown => structuredClone(raw);
const text = (raw: unknown): raw is string =>
  typeof raw === 'string' && !!raw.trim();
function display(raw: unknown): string {
  if (raw === undefined) return 'Not declared';
  if (raw === null) return 'Explicitly null';
  if (typeof raw === 'string') return raw === '' ? 'Empty saved text' : raw;
  try {
    return JSON.stringify(raw) ?? 'Unsupported saved value';
  } catch {
    return 'Unsupported saved value';
  }
}
function facts(
  raw: unknown,
  fields: readonly Field[],
  prefix: string,
): WorkflowPublishedRoutingFact[] {
  const value = object(raw);
  return fields.map(([key, label]) => ({
    id: `${prefix}:${key}`,
    label,
    presence: presence(value, key),
    value: display(value[key]),
    issues: [],
    raw: copy(value[key]),
  }));
}
function extraIssues(raw: unknown, fields: readonly string[]): string[] {
  return Object.keys(object(raw)).some((key) => !fields.includes(key))
    ? [
        'Additional saved properties are retained without interpreting their meaning.',
      ]
    : [];
}
function duplicateIssues(
  raw: unknown,
  index: number,
  all: readonly unknown[],
  key: string,
  label: string,
): string[] {
  const value = object(raw)[key];
  if (!text(value))
    return [
      `Saved ${label} at index ${index} is missing or has an unsupported shape.`,
    ];
  return all.filter((item) => object(item)[key] === value).length === 1
    ? []
    : [
        `Duplicate saved ${label}: ${value}. Every indexed declaration is retained independently.`,
      ];
}
function detail(
  raw: unknown,
  index: number,
  prefix: string,
  label: string,
  fields: readonly Field[],
  issues: readonly string[] = [],
): WorkflowPublishedRoutingDetailRow {
  return {
    id: `${prefix}-${index}`,
    index,
    label,
    fields: facts(raw, fields, `${prefix}-${index}`),
    issues: [
      ...(!record(raw)
        ? [
            'This saved entry has an unsupported shape. Its original value is retained.',
          ]
        : []),
      ...issues,
    ],
    raw: copy(raw),
  };
}
function objectGroup(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
  fields: readonly Field[],
  issues: readonly string[] = [],
): WorkflowPublishedRoutingDetailGroup {
  const raw = owner[key],
    savedPresence = presence(owner, key);
  const rows =
    savedPresence === 'value'
      ? [
          detail(raw, 0, `${prefix}:${key}`, label, fields, [
            ...issues,
            ...extraIssues(
              raw,
              fields.map(([name]) => name),
            ),
          ]),
        ]
      : [];
  return {
    id: `${prefix}:${key}`,
    label,
    presence: savedPresence,
    state:
      savedPresence !== 'value'
        ? 'absent'
        : rows.some((row) => row.issues.length)
          ? 'retained-unknown'
          : 'available',
    rows,
    issues: [],
    raw: copy(raw),
  };
}
function collection(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
  project: (
    raw: unknown,
    index: number,
    all: readonly unknown[],
  ) => WorkflowPublishedRoutingDetailRow,
): WorkflowPublishedRoutingDetailGroup {
  const raw = owner[key],
    savedPresence = presence(owner, key),
    rows = Array.isArray(raw)
      ? raw.map((item, index) => project(item, index, raw))
      : [];
  const issues =
    savedPresence === 'value' && !Array.isArray(raw)
      ? [
          'This saved collection has an unsupported shape. Its original value is retained.',
        ]
      : [];
  return {
    id: `${prefix}:${key}`,
    label,
    presence: savedPresence,
    state:
      savedPresence !== 'value'
        ? 'absent'
        : issues.length || rows.some((row) => row.issues.length)
          ? 'retained-unknown'
          : rows.length
            ? 'available'
            : 'empty',
    rows,
    issues,
    raw: copy(raw),
  };
}
const labels: Record<WorkflowPublishedRoutingSectionId, string> = {
  paths: 'Saved paths',
  condition: 'Routing condition',
  commandResults: 'Command results',
  automation: 'Automated activity',
  terminalResult: 'Terminal result',
};
const nodeFields: readonly Field[] = [
  ['id', 'Node ID'],
  ['typeCode', 'Saved node kind'],
  ['displayName', 'Saved node name'],
  ['stepCode', 'Step code'],
  ['displayOrder', 'Display order'],
];
const edgeFields: readonly Field[] = [
  ['id', 'Edge ID'],
  ['fromNodeId', 'From node ID'],
  ['toNodeId', 'To node ID'],
  ['outcomeCode', 'Saved outcome code'],
  ['pathKindCode', 'Saved path kind'],
];
const outcomeFields: readonly Field[] = [
  ['code', 'Outcome code'],
  ['label', 'Saved outcome label'],
  ['reasonPolicy', 'Saved reason policy'],
  ['destinationNodeId', 'Destination node ID'],
];
function uniqueNode(
  nodes: unknown,
  id: unknown,
): { raw: Saved; index: number } | null {
  if (!text(id) || !Array.isArray(nodes)) return null;
  const matches = nodes
    .map((raw, index) => ({ raw, index }))
    .filter((item) => object(item.raw)['id'] === id);
  return matches.length === 1 && record(matches[0].raw)
    ? { raw: matches[0].raw, index: matches[0].index }
    : null;
}
function endpointFacts(
  prefix: string,
  label: string,
  target: ReturnType<typeof uniqueNode>,
): WorkflowPublishedRoutingFact[] {
  return target
    ? [
        {
          id: `${prefix}:index`,
          label: `Saved ${label} index`,
          presence: 'value',
          value: String(target.index),
          issues: [],
          raw: target.index,
        },
        {
          id: `${prefix}:target`,
          label: `Unique saved ${label}`,
          presence: 'value',
          value: display(target.raw['displayName']),
          issues: [],
          raw: copy(target.raw),
        },
      ]
    : [];
}
function row(
  raw: unknown,
  index: number,
  prefix: string,
  label: string,
  fields: readonly Field[],
  groups: readonly WorkflowPublishedRoutingDetailGroup[] = [],
  issues: readonly string[] = [],
): WorkflowPublishedRoutingRow {
  const base = detail(raw, index, prefix, label, fields, issues);
  return {
    ...base,
    description:
      'Literal saved declarations only. No executable route, current condition, provider availability or default is inferred.',
    state:
      base.issues.length ||
      groups.some((group) => group.state === 'retained-unknown')
        ? 'retained-unknown'
        : 'available',
    groups,
  };
}
function section(
  id: WorkflowPublishedRoutingSectionId,
  owner: Saved,
  key: string,
  rows: WorkflowPublishedRoutingRow[],
  issues: readonly string[] = [],
): WorkflowPublishedRoutingSection {
  const savedPresence = presence(owner, key),
    raw = owner[key];
  return {
    id,
    label: labels[id],
    presence: savedPresence,
    rows,
    issues,
    raw: copy(raw),
    state:
      savedPresence !== 'value'
        ? 'absent'
        : issues.length || rows.some((row) => row.state === 'retained-unknown')
          ? 'retained-unknown'
          : Array.isArray(raw) && !raw.length
            ? 'empty'
            : 'available',
  };
}
function commandReference(
  owner: Saved,
  key: string,
  prefix: string,
  document: Saved,
): WorkflowPublishedRoutingDetailGroup {
  const raw = owner[key],
    ref = object(raw),
    capability = object(ref['key']);
  const group = objectGroup(
    owner,
    key,
    'Saved exact command reference',
    prefix,
    [
      ['key', 'Capability key'],
      ['inputSchemaHash', 'Input schema hash'],
      ['outputSchemaHash', 'Output schema hash'],
      ['contractHash', 'Contract hash'],
    ],
  );
  if (group.rows.length) {
    const current = group.rows[0];
    current.fields = [
      ...facts(
        capability,
        [
          ['provider', 'Provider'],
          ['kind', 'Kind'],
          ['code', 'Code'],
          ['version', 'Version'],
        ],
        `${prefix}:${key}:key`,
      ),
      ...current.fields,
    ];
    if (
      !record(raw) ||
      !record(ref['key']) ||
      !text(capability['provider']) ||
      capability['kind'] !== 'command' ||
      !text(capability['code']) ||
      !Number.isSafeInteger(capability['version']) ||
      Number(capability['version']) < 1 ||
      !['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every((name) =>
        text(ref[name]),
      )
    )
      current.issues = [
        ...current.issues,
        'The saved command reference is incomplete or unsupported. No installed reference is substituted.',
      ];
    const pins = document['capabilityReferences'];
    const sameKey = (item: unknown) => {
      const candidate = object(object(item)['key']);
      return ['provider', 'kind', 'code', 'version'].every(
        (name) => candidate[name] === capability[name],
      );
    };
    const matches = Array.isArray(pins) ? pins.filter(sameKey) : [];
    if (
      matches.length !== 1 ||
      !['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every(
        (name) => object(matches[0])[name] === ref[name],
      )
    )
      current.issues = [
        ...current.issues,
        'One exact saved capability pin is not established. Current installation and compatibility are not evaluated.',
      ];
    current.issues = [
      ...current.issues,
      ...extraIssues(capability, ['provider', 'kind', 'code', 'version']),
    ];
    if (current.issues.length) group.state = 'retained-unknown';
  }
  return group;
}
function pathSection(
  document: Saved,
  nodes: unknown,
  target: WorkflowPublishedRoutingTarget,
  rawElement: unknown,
  index: number,
): WorkflowPublishedRoutingSection {
  const edges = document['edges'];
  let selected: { raw: unknown; index: number }[] = [];
  const issues: string[] = [];
  if (target.kind === 'edge') selected = [{ raw: rawElement, index }];
  else if (uniqueNode(nodes, object(rawElement)['id']))
    selected = Array.isArray(edges)
      ? edges
          .map((raw, index) => ({ raw, index }))
          .filter(
            (item) =>
              object(item.raw)['fromNodeId'] === target.nodeId ||
              object(item.raw)['toNodeId'] === target.nodeId,
          )
      : [];
  else
    issues.push(
      'The selected node ID does not identify one saved node. Associated paths are not guessed; the original edge collection is retained.',
    );
  if (presence(document, 'edges') === 'value' && !Array.isArray(edges))
    issues.push(
      'The saved edge collection has an unsupported shape. Its original value is retained.',
    );
  const rows = selected.map((item) => {
    const value = object(item.raw),
      from = uniqueNode(nodes, value['fromNodeId']),
      to = uniqueNode(nodes, value['toNodeId']);
    const result = row(
      item.raw,
      item.index,
      'path',
      text(value['id']) ? display(value['id']) : `Saved edge ${item.index + 1}`,
      edgeFields,
      [],
      [
        ...duplicateIssues(
          item.raw,
          item.index,
          Array.isArray(edges) ? edges : [],
          'id',
          'edge ID',
        ),
        ...(!from
          ? [
              'The saved source endpoint is missing or ambiguous. No source caption is guessed.',
            ]
          : []),
        ...(!to
          ? [
              'The saved destination endpoint is missing or ambiguous. No destination caption is guessed.',
            ]
          : []),
        ...(value['pathKindCode'] != null && value['pathKindCode'] !== 'return'
          ? [
              'The saved path kind is unknown and is not treated as an ordinary or return route.',
            ]
          : []),
      ],
    );
    result.fields = [
      ...result.fields,
      ...endpointFacts(`path-${item.index}:from`, 'source node', from),
      ...endpointFacts(`path-${item.index}:to`, 'destination node', to),
    ];
    return result;
  });
  return section('paths', document, 'edges', rows, issues);
}
function conditionSection(node: Saved): WorkflowPublishedRoutingSection {
  const raw = node['routing'],
    value = object(raw),
    fields: readonly Field[] = [
      ['conditionCode', 'Condition code'],
      ['conditionVersion', 'Condition version'],
    ];
  const issues = [
    ...(node['typeCode'] !== 'routing_decision'
      ? [
          'This condition is saved on a different node kind. It is retained without relocation.',
        ]
      : []),
    ...(!text(value['conditionCode']) ||
    !Number.isSafeInteger(value['conditionVersion']) ||
    Number(value['conditionVersion']) < 1
      ? [
          'The saved condition binding is unsupported. No current condition metadata is loaded.',
        ]
      : []),
  ];
  return section(
    'condition',
    node,
    'routing',
    presence(node, 'routing') === 'value'
      ? [
          row(
            raw,
            0,
            'condition',
            'Saved routing condition',
            fields,
            [],
            issues,
          ),
        ]
      : [],
  );
}
function commandResultsSection(
  node: Saved,
  document: Saved,
  nodes: unknown,
): WorkflowPublishedRoutingSection {
  const declaration = node['declaration'],
    behavior = object(declaration)['behavior'];
  if (
    (declaration != null && !record(declaration)) ||
    (behavior != null && !record(behavior))
  )
    return {
      id: 'commandResults',
      label: labels.commandResults,
      state: 'retained-unknown',
      presence: 'value',
      rows: [],
      issues: [
        'The saved command owner is opaque. No empty result list is inferred.',
      ],
      raw: copy(
        declaration != null && !record(declaration) ? declaration : behavior,
      ),
    };
  if (declaration == null || behavior == null)
    return {
      id: 'commandResults',
      label: labels.commandResults,
      state: 'absent',
      presence:
        declaration == null
          ? presence(node, 'declaration')
          : presence(object(declaration), 'behavior'),
      rows: [],
      issues: [],
      raw: copy(declaration == null ? declaration : behavior),
    };
  const owner = object(behavior),
    commands = owner['commands'];
  const rows = Array.isArray(commands)
    ? commands.map((raw, index) => {
        const command = object(raw),
          prefix = `command-result-${index}`;
        const outcomes = collection(
          command,
          'outcomes',
          'Saved command outcomes',
          prefix,
          (item, outcomeIndex, all) => {
            const value = object(item),
              target = uniqueNode(nodes, value['destinationNodeId']);
            const outcome = detail(
              item,
              outcomeIndex,
              `${prefix}:outcome`,
              text(value['label'])
                ? display(value['label'])
                : `Saved result ${outcomeIndex + 1}`,
              outcomeFields,
              [
                ...duplicateIssues(
                  item,
                  outcomeIndex,
                  all,
                  'code',
                  'outcome code',
                ),
                ...(value['destinationNodeId'] !== '' && !target
                  ? [
                      'The saved destination does not identify one node. No route or default is substituted.',
                    ]
                  : []),
                ...(value['reasonPolicy'] !== 'optional' &&
                value['reasonPolicy'] !== 'required'
                  ? ['The saved reason policy is retained without a default.']
                  : []),
              ],
            );
            outcome.fields = [
              ...outcome.fields,
              ...endpointFacts(
                `${prefix}:outcome-${outcomeIndex}:destination`,
                'destination node',
                target,
              ),
            ];
            return outcome;
          },
        );
        const result = row(
          raw,
          index,
          'command-result',
          `Saved command ${index + 1}`,
          [
            ['capability', 'Capability'],
            ['requirements', 'Requirements'],
            ['outcomes', 'Outcomes'],
            ['captureForm', 'Capture form'],
            ['formConfiguration', 'Form configuration'],
            ['inputBindings', 'Input bindings'],
            ['outputBindings', 'Output bindings'],
          ],
          [commandReference(command, 'capability', prefix, document), outcomes],
          node['typeCode'] !== 'human_step'
            ? [
                'These command declarations are saved on a different node kind and are not relocated.',
              ]
            : [],
        );
        result.fields = [];
        return result;
      })
    : [];
  // Each command's full raw is retained; its other Work settings are outside this result projection.
  return section(
    'commandResults',
    owner,
    'commands',
    rows,
    presence(owner, 'commands') === 'value' && !Array.isArray(commands)
      ? [
          'The saved command collection is unsupported. Its original value is retained.',
        ]
      : [],
  );
}
function automationSection(
  node: Saved,
  document: Saved,
): WorkflowPublishedRoutingSection {
  const raw = node['automation'],
    automation = object(raw),
    prefix = 'automation-0';
  const inputs = collection(
    automation,
    'inputs',
    'Saved automatic inputs',
    prefix,
    (item, index, all) =>
      detail(
        item,
        index,
        `${prefix}:input`,
        `Saved input ${index + 1}`,
        [
          ['field', 'Field'],
          ['source', 'Source'],
          ['value', 'Saved literal value'],
          ['context', 'Context field'],
          ['literalJson', 'Saved literal JSON'],
        ],
        duplicateIssues(item, index, all, 'field', 'input field'),
      ),
  );
  const mappings = collection(
    automation,
    'outcomes',
    'Saved automatic result mappings',
    prefix,
    (item, index, all) =>
      detail(
        item,
        index,
        `${prefix}:mapping`,
        `Saved mapping ${index + 1}`,
        [
          ['commandOutcome', 'Command outcome'],
          ['routeOutcome', 'Route outcome'],
        ],
        [
          ...duplicateIssues(
            item,
            index,
            all,
            'commandOutcome',
            'command outcome',
          ),
          ...duplicateIssues(item, index, all, 'routeOutcome', 'route outcome'),
        ],
      ),
  );
  const fields: readonly Field[] = [
    ['version', 'Saved automation version'],
    ['command', 'Command'],
    ['inputs', 'Inputs'],
    ['outcomes', 'Outcomes'],
  ];
  const rows =
    presence(node, 'automation') === 'value'
      ? [
          row(
            raw,
            0,
            'automation',
            'Saved automatic activity',
            fields,
            [
              commandReference(automation, 'command', prefix, document),
              inputs,
              mappings,
            ],
            [
              ...(automation['version'] !== 1
                ? [
                    'This saved automation version is retained without interpreting it as version 1.',
                  ]
                : []),
              ...(node['typeCode'] !== 'automated_step'
                ? [
                    'This automation is saved on a different node kind. It is not executed or relocated.',
                  ]
                : []),
            ],
          ),
        ]
      : [];
  if (rows.length)
    rows[0].fields = facts(
      raw,
      [['version', 'Saved automation version']],
      prefix,
    );
  return section('automation', node, 'automation', rows);
}
function terminalSection(node: Saved): WorkflowPublishedRoutingSection {
  const raw = node['terminalOutcome'],
    value = object(raw);
  return section(
    'terminalResult',
    node,
    'terminalOutcome',
    presence(node, 'terminalOutcome') === 'value'
      ? [
          row(
            raw,
            0,
            'terminal-result',
            'Saved terminal result',
            [
              ['version', 'Saved result version'],
              ['code', 'Saved result code'],
              ['label', 'Saved result label'],
            ],
            [],
            [
              ...(node['typeCode'] !== 'terminal'
                ? [
                    'This result is saved on a different node kind. It is retained without relocation.',
                  ]
                : []),
              ...(value['version'] !== 1 ||
              !text(value['code']) ||
              !text(value['label'])
                ? [
                    'The saved terminal result is unsupported. No child outcome or default is generated.',
                  ]
                : []),
            ],
          ),
        ]
      : [],
  );
}
/** Saved graph declarations only; no route selection, condition evaluation or browser canonical verification. */
export function readWorkflowPublishedRoutingInspection(
  source: WorkflowPublishedInspectionSource,
  target: WorkflowPublishedRoutingTarget,
): WorkflowPublishedRoutingInspection {
  const admitted = readWorkflowPublishedInspection(source);
  const unavailable = (
    id: WorkflowPublishedRoutingSectionId,
  ): WorkflowPublishedRoutingSection => ({
    id,
    label: labels[id],
    state: 'unavailable',
    presence: 'absent',
    rows: [],
    issues: [],
    raw: null,
  });
  const sections: Record<
    WorkflowPublishedRoutingSectionId,
    WorkflowPublishedRoutingSection
  > = {
    paths: unavailable('paths'),
    condition: unavailable('condition'),
    commandResults: unavailable('commandResults'),
    automation: unavailable('automation'),
    terminalResult: unavailable('terminalResult'),
  };
  const result: WorkflowPublishedRoutingInspection = {
    context: JSON.stringify([admitted.context, target]),
    state: 'unavailable',
    provenance: structuredClone(admitted.provenance),
    target: { ...target },
    element: null,
    node: null,
    issues: [...admitted.issues],
    sections,
  };
  if (admitted.state !== 'available') {
    result.issues = [
      ...admitted.issues,
      'Saved routing and result content is withheld because the canonical document was not admitted.',
    ];
    return result;
  }
  const document = object(source.definitionDocument),
    nodes = document['nodes'],
    collection = target.kind === 'node' ? nodes : document['edges'];
  const index = target.kind === 'node' ? target.nodeIndex : target.edgeIndex,
    expected = target.kind === 'node' ? target.nodeId : target.edgeId;
  if (
    !Number.isSafeInteger(index) ||
    index < 0 ||
    !Array.isArray(collection) ||
    index >= collection.length
  ) {
    result.issues = [
      'The exact saved element index is unavailable. No other element is substituted.',
    ];
    return result;
  }
  const raw = collection[index],
    value = object(raw),
    id = typeof value['id'] === 'string' ? value['id'] : null;
  if (id !== expected) {
    result.issues = [
      'The saved element index and identity do not match. Routing and results are withheld.',
    ];
    return result;
  }
  result.state = 'available';
  result.context = JSON.stringify([admitted.context, target, document]);
  const issues = [
    ...(!record(raw)
      ? ['The exact saved element is opaque. Its original value is retained.']
      : []),
    ...duplicateIssues(raw, index, collection, 'id', `${target.kind} ID`),
  ];
  result.issues = issues;
  result.element = {
    kind: target.kind,
    index,
    id,
    label:
      target.kind === 'node' && typeof value['displayName'] === 'string'
        ? display(value['displayName'])
        : text(id)
          ? id
          : `Saved ${target.kind} ${index + 1}`,
    issues: [...issues],
    raw: copy(raw),
  };
  sections.paths = pathSection(document, nodes, target, raw, index);
  const owner =
    target.kind === 'node' && record(raw)
      ? { raw: value, index }
      : target.kind === 'edge'
        ? uniqueNode(nodes, value['fromNodeId'])
        : null;
  if (!owner) {
    for (const key of [
      'condition',
      'commandResults',
      'automation',
      'terminalResult',
    ] as const)
      sections[key] = {
        ...sections[key],
        issues: [
          'The exact saved source node is opaque, missing or ambiguous. Result ownership is not guessed.',
        ],
      };
    return result;
  }
  const node = owner.raw,
    nodeId = typeof node['id'] === 'string' ? node['id'] : null,
    nodeIssues =
      typeof node['typeCode'] !== 'string' ||
      ![
        'entry',
        'human_step',
        'automated_step',
        'routing_decision',
        'terminal',
      ].includes(node['typeCode'])
        ? [
            'The saved node kind is unknown. All declarations remain literal and retained.',
          ]
        : [];
  result.node = {
    index: owner.index,
    id: nodeId,
    label:
      typeof node['displayName'] === 'string'
        ? display(node['displayName'])
        : `Saved node ${owner.index + 1}`,
    typeCode: display(node['typeCode']),
    issues: nodeIssues,
    raw: copy(node),
  };
  sections.condition = conditionSection(node);
  sections.commandResults = commandResultsSection(node, document, nodes);
  sections.automation = automationSection(node, document);
  sections.terminalResult = terminalSection(node);
  return result;
}
